import { JON_BOAT, OUTBOARD, SINGLE, FLAT, NON_POWERED, AREA_TYPES } from 'constants/boatApplication';
import { _initBoatSystems } from './BaseBoat';
import moment from 'moment';

const initState = {
  id: 'new',
  boatType: JON_BOAT,
  status: 'DRAFT',
  engineType: null,
  boatSpecifications: {},
  boatSystems: [],
  cubicCapacity: {},
  floatationCapacity: {},
  additionalFiles: [],
};

export default class JonBoat {
  constructor(boat = initState) {
    this.id = boat.id || initState.id;
    this.boatType = JON_BOAT;
    this.status = boat.status || initState.status;
    this.engineType = boat.engineType;
    this.plateType = boat.plateType;
    this.dualSteering = _initDualSteering.call(this, boat);
    this.boatSpecifications = _initBoatSpecifications.call(this, boat.boatSpecifications || boat);
    this.boatSystems = _initBoatSystems(boat.boatSystems);
    this.specificationsRequiredFieldsList = getSpecificationsRequiredFieldsList.call(this);
    this.cubicCapacity = _initCubicCapacity(boat.cubicCapacity || initState.cubicCapacity);
    this.personPowerCapacity = _initPersonPowerCapacity.call(this, boat.personPowerCapacity ? {...boat.personPowerCapacity, dualSteering:boat.dualSteering} : boat);
    this.powerCapacity = _initPowerCapacity(boat.powerCapacity || boat);
    this.floatationCapacity = _initFloatationCapacity(boat.floatationCapacity);
    this.additionalFiles = boat.additionalFiles ? [...boat.additionalFiles] : initState.additionalFiles;
    this.isFloatationTestReportRequired = boat.boatSpecifications ? boat.boatSpecifications.lengthFeet < 20 : boat.lengthFeet < 20;
    this.bottomContourList =
      [
        {name: 'Flat', value: FLAT},
      ];
    this.engineTypesList =
      [
        OUTBOARD,
        NON_POWERED
      ];
    this.engineSubTypesList =
      [
        {name: 'Up to 6 HP.', value: 'SUB_TYPE_1'},
        {name: 'Over 6 HP. and up to 20 HP.', value: 'SUB_TYPE_2'},
        {name: 'Over 20 HP. and up to 100 HP.', value: 'SUB_TYPE_3'},
        {name: 'Over 100 HP.', value: 'SUB_TYPE_4'},
      ];
  }
}

function getSpecificationsRequiredFieldsList() {
  return [
    'name',
    'year',
    'cityAndStateForCapacityTag',
    'lengthFeet',
    'fuelTankCapacity',
    'widthFeet',
    'weightOfFinishedBoat',
    'transformHeightAtCenterline',
  ];
}

function _initBoatSpecifications(boat) {
  let boatSpecifications = {
    name: boat.name || '',
    year: boat.year || '',
    cityAndStateForCapacityTag: boat.cityAndStateForCapacityTag || '',
    lengthFeet: boat.lengthFeet === undefined ? '' : boat.lengthFeet,
    lengthInches: boat.lengthInches === undefined ? '' : boat.lengthInches,
    bottomContour: 'FLAT',
    fuelTankCapacity: boat.fuelTankCapacity === undefined ? '' : boat.fuelTankCapacity,
    widthFeet: boat.widthFeet === undefined ? '' : boat.widthFeet,
    widthInches: boat.widthInches === undefined ? '' : boat.widthInches,
    weightOfFinishedBoat: boat.weightOfFinishedBoat === undefined ? '' : boat.weightOfFinishedBoat,
    transformHeightAtCenterline: boat.transformHeightAtCenterline === undefined ? '' : boat.transformHeightAtCenterline,
  };
  if (this.engineType === OUTBOARD) {
    boatSpecifications.motorType = boat.motorType || SINGLE;
    boatSpecifications.hasElectricPropulsion = !!boat.hasElectricPropulsion;
  }

  return boatSpecifications;
}

function _initPersonPowerCapacity(boat) {
  const capacity = {
    plateType: boat.plateType || '',
    calBoatWeight: boat.calBoatWeight === undefined ? '' : boat.calBoatWeight,

    reqMaxNumberOfPersons: boat.reqMaxNumberOfPersons === undefined ? '' : boat.reqMaxNumberOfPersons,
    reqMaxPersonsWeight: boat.reqMaxPersonsWeight === undefined ? '' : boat.reqMaxPersonsWeight,
    reqMaxWeight: boat.reqMaxWeight === undefined ? '' : boat.reqMaxWeight,

    calMaxNumberOfPersons: boat.calMaxNumberOfPersons === undefined ? '' : boat.calMaxNumberOfPersons,
    calMaxPersonsWeight: boat.calMaxPersonsWeight === undefined ? '' : boat.calMaxPersonsWeight,
    calMaxWeight: boat.calMaxWeight === undefined ? '' : boat.calMaxWeight,

    appMaxWeight: boat.appMaxWeight === undefined ? '' : boat.appMaxWeight,
    appMaxNumberOfPersons: boat.appMaxNumberOfPersons === undefined ? '' : boat.appMaxNumberOfPersons,
    appMaxPersonsWeight: boat.appMaxPersonsWeight === undefined ? '' : boat.appMaxPersonsWeight,

    overrideApprovedValues:boat.overrideApprovedValues,
  };

  if (this.engineType === OUTBOARD) {
    capacity.reqMaxHP = boat.reqMaxHP === undefined ? '' : boat.reqMaxHP;
    capacity.calcMaxHP = boat.calcMaxHP === undefined ? '' : boat.calcMaxHP;
    capacity.apprMaxHP = boat.apprMaxHP === undefined ? '' : boat.apprMaxHP;
  }

  if (boat.dualSteering) {
    capacity.reqMaxHPDualSteering = boat.reqMaxHPDualSteering || '';
    capacity.calcMaxHPDualSteering = boat.calcMaxHPDualSteering || '';
    capacity.apprMaxHPDualSteering = boat.apprMaxHPDualSteering || '';
  }

  return capacity;
}

function _initPowerCapacity(boat) {
  return {
    maxSpeedLessThan30: boat.maxSpeedLessThan30 !== undefined ? boat.maxSpeedLessThan30 : true,
    passesTestAtFullPower: !!boat.passesTestAtFullPower,
    maneuveringSpeed: boat.maneuveringSpeed === undefined ? '' : boat.maneuveringSpeed
  };
}

function _initFloatationCapacity(floatation) {
  if (floatation && floatation.length) {
    return {
      testDate: moment(floatation[0].testDate).utc(),
      files: floatation
    };
  } else {
    return {};
  }
}

function _initDualSteering(boat) {
  if (this.engineType !== OUTBOARD) return undefined;
  return boat.dualSteering !== undefined ? boat.dualSteering : false;
}

function _initCubicCapacity(boat) {
  let cubicCapacity = {};

  if (boat.areas && boat.areas.length !== undefined) {
    let loadedAreas = {};
    boat.areas.forEach(element => {
      loadedAreas[element.type] = element;
    });
    cubicCapacity.areas = loadedAreas;
    cubicCapacity.isAreas = boat.manualAreas === undefined ? false : boat.manualAreas;
    cubicCapacity.isManual = boat.manualCubicCapacity === undefined ? true : boat.manualCubicCapacity;
  } else if (boat.areas) {
    cubicCapacity.areas = boat.areas;
    cubicCapacity.isAreas = boat.isAreas === undefined ? false : boat.isAreas;
    cubicCapacity.isManual = boat.isManual === undefined ? true : boat.isManual;
  } else {
    cubicCapacity.areas = {
      [AREA_TYPES.AA_EIGHTH_LENGTH_FORWARD]: {},
      [AREA_TYPES.A_QTR_LENGTH_FORWARD]: {},
      [AREA_TYPES.B_AMIDSHIPS]: {},
      [AREA_TYPES.C_QTR_LENGTH_AFT]: {},
      [AREA_TYPES.D_TRANSOM]: {}
    };
    cubicCapacity.isAreas = false;
    cubicCapacity.isManual = true;
  }

  cubicCapacity.cubicCapacity = boat.cubicCapacity === undefined ? '' : boat.cubicCapacity;
  cubicCapacity.calculationLength = boat.calculationLength === undefined ? '' : boat.calculationLength;
  cubicCapacity.volumeWithinMotor = boat.volumeWithinMotor === undefined ? '' : boat.volumeWithinMotor;

  return cubicCapacity;
}



// WEBPACK FOOTER //
// ./src/app/store/models/JonBoat.js